import {
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  getDay,
  isSameMonth,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import React, { FC } from 'react';
import { twMerge } from 'tailwind-merge';

import { Popover, PopoverContent, PopoverTrigger } from '../../../components/popover/index';
import {
  HolidayRegionColor,
  HolidayRegionFlag,
  HolidayRegionTitle,
  PublicHoliday,
  TeamMember,
  Vacation,
} from '../../../types/team.types';
import { MemberAvatar } from '../member-avatar';
import { formatIsoDate, toIsoDate } from '../team-date.util';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MAX_VISIBLE_CHIPS = 3;

const isWeekendDay = (day: Date): boolean => {
  const weekday = getDay(day);
  return weekday === 0 || weekday === 6;
};

export type CalendarViewProps = {
  holidays: PublicHoliday[];
  members: TeamMember[];
  onEditHoliday: (holiday: PublicHoliday) => void;
  onEditVacation: (vacation: Vacation) => void;
  personFilter: string;
  vacations: Vacation[];
  viewDate: Date;
};

export const CalendarView: FC<CalendarViewProps> = ({
  viewDate,
  vacations,
  holidays,
  members,
  personFilter,
  onEditVacation,
  onEditHoliday,
}) => {
  const memberById = new Map(members.map((member) => [member.id, member]));
  const resolveMember = (vacation: Vacation): TeamMember =>
    vacation.teamMember ?? memberById.get(vacation.teamMemberId)!;

  const monthStart = startOfMonth(viewDate);
  const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const gridEnd = endOfWeek(endOfMonth(viewDate), { weekStartsOn: 1 });
  const days = eachDayOfInterval({ start: gridStart, end: gridEnd });

  const todayStr = toIsoDate(new Date());

  const visibleVacations = vacations.filter(
    (vacation) => personFilter === 'all' || vacation.teamMemberId === personFilter,
  );

  return (
    <div className="overflow-hidden rounded-lg border border-gray-100 bg-white">
      <div className="grid grid-cols-[repeat(5,minmax(0,1fr))_repeat(2,minmax(0,0.5fr))] border-b border-gray-100 bg-gray-50">
        {WEEKDAYS.map((weekday, index) => (
          <div
            className={twMerge(
              'px-2 py-2 text-center text-xs font-[550] text-gray-500',
              (index === 5 || index === 6) && 'text-gray-400',
            )}
            key={weekday}
          >
            {weekday}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-[repeat(5,minmax(0,1fr))_repeat(2,minmax(0,0.5fr))]">
        {days.map((day) => {
          const dayStr = toIsoDate(day);
          const inMonth = isSameMonth(day, viewDate);
          const isToday = dayStr === todayStr;
          const isWeekend = isWeekendDay(day);

          const dayHolidays = holidays.filter((holiday) => holiday.date === dayStr);
          // Weekends are non-working days — vacations aren't shown on them (only holidays remain).
          const dayVacations = isWeekend
            ? []
            : visibleVacations.filter(
                (vacation) => vacation.startDate <= dayStr && dayStr <= vacation.endDate,
              );
          const shownVacations = dayVacations.slice(0, MAX_VISIBLE_CHIPS);
          const extraVacations = dayVacations.slice(MAX_VISIBLE_CHIPS);

          return (
            <div
              className={twMerge(
                'flex min-h-[116px] flex-col gap-1 border-b border-r border-gray-100 p-1.5',
                isWeekend && 'bg-gray-100',
                // Leading/trailing days from the adjacent month recede so they aren't mistaken for
                // the current month when navigating.
                !inMonth && 'bg-gray-50 opacity-40',
                // Today: a light tint (in addition to the blue date badge) — noticeable, not loud.
                isToday && 'bg-brand-25',
              )}
              key={dayStr}
            >
              <div className="flex justify-end">
                <span
                  className={twMerge(
                    'flex size-6 items-center justify-center rounded-full text-xs font-[450]',
                    inMonth ? 'text-gray-600' : 'text-gray-300',
                    isWeekend && inMonth && 'text-gray-400',
                    isToday && 'bg-brand-700 font-[550] text-white',
                  )}
                >
                  {format(day, 'd')}
                </span>
              </div>

              {dayHolidays.map((holiday) => (
                <button
                  className="flex w-full items-center gap-1 truncate rounded px-1.5 py-0.5 text-left text-[10px] font-[550] text-white"
                  key={holiday.id}
                  onClick={() => onEditHoliday(holiday)}
                  style={{ backgroundColor: HolidayRegionColor[holiday.region] }}
                  title={`${holiday.name} · ${HolidayRegionTitle[holiday.region]}`}
                  type="button"
                >
                  <span>{HolidayRegionFlag[holiday.region]}</span>
                  <span className="truncate">{holiday.name}</span>
                </button>
              ))}

              {dayVacations.length > 0 && (
                <div className="mt-auto flex flex-col gap-1">
                  {shownVacations.map((vacation) => {
                    const member = resolveMember(vacation);

                    return (
                      <button
                        className="flex w-full items-center rounded px-1.5 py-0.5 text-left text-[10px] font-[550] text-white outline-none ring-brand-300 focus:ring-2"
                        key={vacation.id}
                        onClick={() => onEditVacation(vacation)}
                        style={{ backgroundColor: member.color }}
                        title={member.name}
                        type="button"
                      >
                        <span className="truncate">{member.name}</span>
                      </button>
                    );
                  })}

                  {extraVacations.length > 0 && (
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          className="flex h-5 w-full items-center justify-center rounded bg-gray-200 text-[10px] font-[550] text-gray-600 hover:bg-gray-300"
                          type="button"
                        >
                          +{extraVacations.length} more
                        </button>
                      </PopoverTrigger>
                      <PopoverContent
                        align="start"
                        className="w-60 flex-col items-stretch gap-1 p-2"
                      >
                        <span className="mb-1 text-xs font-[550] text-gray-700">
                          Off on {formatIsoDate(dayStr)}
                        </span>
                        {dayVacations.map((vacation) => (
                          <button
                            className="flex items-center gap-2 rounded p-1 text-left hover:bg-gray-50"
                            key={vacation.id}
                            onClick={() => onEditVacation(vacation)}
                            type="button"
                          >
                            <MemberAvatar member={resolveMember(vacation)} size={22} />
                            <span className="text-xs font-[450] text-gray-700">
                              {resolveMember(vacation).name}
                            </span>
                          </button>
                        ))}
                      </PopoverContent>
                    </Popover>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
